import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from '../../../utils/classNames';
import { mapEventToStatusVariant, mapViewerStatusToVariant } from '../../../utils/eventPresentation';
import { getCellKey } from '../../../utils/eventsCalendar';
import './index.scss';

const EVENT_DRAG_MIME = 'application/x-muzone-calendar-event';

const VARIANT_TO_DOT = {
    going: 'event-calendar-day__event--going',
    interested: 'event-calendar-day__event--interested',
    pending: 'event-calendar-day__event--pending',
    cancel: 'event-calendar-day__event--cancel',
    decline: 'event-calendar-day__event--decline',
    publish: 'event-calendar-day__event--publish',
    complete: 'event-calendar-day__event--complete',
};

function resolveEventVariant(event) {
    if (!event) return 'publish';
    const eventStatusVariant = mapEventToStatusVariant(event.status);
    if (eventStatusVariant === 'cancel' || eventStatusVariant === 'complete') {
        return eventStatusVariant;
    }
    if (event.viewerAttendanceStatus) {
        return mapViewerStatusToVariant(event.viewerAttendanceStatus);
    }
    return eventStatusVariant;
}

export default function EventCalendarDay({
    date,
    events = [],
    isCurrentMonth = true,
    isToday = false,
    onEventClick,
    onDateSelect,
    onEventDrop,
    canSelectDate = false,
    canMutateEvents = false,
    maxVisibleEvents = 2,
    className = '',
}) {
    const { t } = useTranslation();
    const eventsGroupId = useId().replace(/:/g, '');
    const [isExpanded, setIsExpanded] = useState(false);
    const dayNumber = date ? date.getDate() : '';
    const dayKey = getCellKey(date);
    const visibleEvents = events.slice(0, maxVisibleEvents);
    const overflow = Math.max(0, events.length - maxVisibleEvents);
    const overflowEvents = isExpanded ? events.slice(maxVisibleEvents) : [];
    const canCreateOnDate = canSelectDate && typeof onDateSelect === 'function';
    const canDropEvents = canMutateEvents && typeof onEventDrop === 'function';

    const handleDateSelect = (event) => {
        event.stopPropagation();
        if (canCreateOnDate) {
            onDateSelect(date);
        }
    };

    const handleEventDragStart = (calendarEvent, action) => (event) => {
        event.stopPropagation();
        if (!canDropEvents || !calendarEvent?.id || !event.dataTransfer) {
            event.preventDefault();
            return;
        }
        const payload = JSON.stringify({
            eventId: String(calendarEvent.id),
            action,
        });
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(EVENT_DRAG_MIME, payload);
        event.dataTransfer.setData('text/plain', String(calendarEvent.id));
    };

    const readDropPayload = (event) => {
        if (!event.dataTransfer) {
            return null;
        }
        const raw = event.dataTransfer.getData(EVENT_DRAG_MIME);
        if (raw) {
            try {
                const payload = JSON.parse(raw);
                if (payload?.eventId) {
                    return {
                        eventId: String(payload.eventId),
                        action: payload.action === 'resize-end' ? 'resize-end' : 'move',
                    };
                }
            } catch (_error) {
                return null;
            }
        }
        const eventId = event.dataTransfer.getData('text/plain');
        return eventId ? { eventId: String(eventId), action: 'move' } : null;
    };

    const handleDragOver = (event) => {
        if (!canDropEvents) {
            return;
        }
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move';
        }
    };

    const handleDrop = (event) => {
        if (!canDropEvents || !event.dataTransfer) {
            return;
        }
        event.preventDefault();
        const payload = readDropPayload(event);
        if (payload) {
            onEventDrop(payload, date);
        }
    };

    const renderEventButton = (event) => {
        const variant = resolveEventVariant(event);
        const title = event?.title || '';

        return (
            <div key={event?.id || title} className="event-calendar-day__event-row">
                <button
                    type="button"
                    draggable={canDropEvents}
                    className={classNames(
                        'event-calendar-day__event',
                        VARIANT_TO_DOT[variant] || VARIANT_TO_DOT.publish,
                    )}
                    onDragStart={handleEventDragStart(event, 'move')}
                    onClick={(clickEvent) => {
                        clickEvent.stopPropagation();
                        if (typeof onEventClick === 'function') {
                            onEventClick(event);
                        }
                    }}
                    aria-label={title}
                >
                    <span className="event-calendar-day__event-dot" aria-hidden="true" />
                    <span className="event-calendar-day__event-label">{title}</span>
                </button>
                {canDropEvents ? (
                    <button
                        type="button"
                        draggable
                        className="event-calendar-day__event-resize"
                        onDragStart={handleEventDragStart(event, 'resize-end')}
                        onClick={(clickEvent) => clickEvent.stopPropagation()}
                        aria-label={`Resize ${title}`}
                    />
                ) : null}
            </div>
        );
    };

    return (
        <div
            className={classNames(
                'event-calendar-day',
                !isCurrentMonth && 'event-calendar-day--muted',
                isToday && 'event-calendar-day--today',
                canDropEvents && 'event-calendar-day--drop-enabled',
                className,
            )}
            data-date-key={dayKey}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {canCreateOnDate ? (
                <button
                    type="button"
                    className="event-calendar-day__num event-calendar-day__num-button"
                    onClick={handleDateSelect}
                    aria-label={`Create event on ${dayKey}`}
                >
                    {dayNumber}
                </button>
            ) : (
                <span className="event-calendar-day__num">{dayNumber}</span>
            )}
            <div id={eventsGroupId} className="event-calendar-day__events">
                {visibleEvents.map(renderEventButton)}
                {overflow > 0 ? (
                    <button
                        type="button"
                        className="event-calendar-day__more"
                        aria-controls={eventsGroupId}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? t('eventsPage.calendar.hideOverflow') : t('eventsPage.calendar.moreEventsAria', { count: overflow })}
                        onClick={(event) => {
                            event.stopPropagation();
                            setIsExpanded((current) => !current);
                        }}
                    >
                        {isExpanded ? t('eventsPage.calendar.hideOverflow') : `+${overflow}`}
                    </button>
                ) : null}
                {overflowEvents.map(renderEventButton)}
            </div>
        </div>
    );
}

EventCalendarDay.propTypes = {
    date: PropTypes.instanceOf(Date).isRequired,
    events: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        startAt: PropTypes.string,
        endAt: PropTypes.string,
        allDay: PropTypes.bool,
        status: PropTypes.string,
        viewerAttendanceStatus: PropTypes.string,
    })),
    isCurrentMonth: PropTypes.bool,
    isToday: PropTypes.bool,
    onEventClick: PropTypes.func,
    onDateSelect: PropTypes.func,
    onEventDrop: PropTypes.func,
    canSelectDate: PropTypes.bool,
    canMutateEvents: PropTypes.bool,
    maxVisibleEvents: PropTypes.number,
    className: PropTypes.string,
};
